import type { NarrationOptions, NarrationService } from '../../sdk/narration';

/**
 * A pass-through narration service that reports what was asked for.
 *
 * Speech is the one part of a round with no visible trace: browser TTS is silent when a family
 * turns narration off, silent when a device has no voice installed, and silent in an automated
 * browser — so "did Bip-Bop say that again?" is otherwise unanswerable from outside. This wraps
 * the real service and reports every request *before* delegating, which makes the repeat control
 * (NAR-01/02, game design spec §31) observable at the seam where it actually happens rather than
 * at the key that asked for it.
 *
 * It decides nothing and changes nothing: every call is forwarded unchanged, and a failure in the
 * observer must not stop a child hearing the instruction.
 */

export interface NarrationActivity {
  /** How many lines have been requested since this observer was created. */
  utterances: number;
  /** The most recent line requested, whether or not a voice was available to say it. */
  lastText: string | null;
  /**
   * The lines requested, most recent last, bounded to the last {@link NARRATION_HISTORY_LIMIT}.
   *
   * A count and a latest line cannot answer the question the repeat control actually raises (S5-8):
   * "did pressing R say **this instruction**, exactly once?". A count moves when the help ladder
   * narrates a hint on its own clock, and the latest line is overwritten by whatever landed most
   * recently — so a repeat that fired twice, fired the wrong line, or did not fire at all while a
   * rung did were all indistinguishable at this seam.
   *
   * Bounded because it is rendered into a DOM attribute and a mission is long; the window only has
   * to be wider than one presentation plus a keypress, and it is many times that.
   */
  history: readonly string[];
}

/** How many lines the readout remembers. See {@link NarrationActivity.history}. */
export const NARRATION_HISTORY_LIMIT = 12;

export type NarrationActivityListener = (activity: NarrationActivity) => void;

export function observeNarration(
  inner: NarrationService,
  onActivity: NarrationActivityListener,
): NarrationService {
  const activity: { utterances: number; lastText: string | null; history: string[] } = {
    utterances: 0,
    lastText: null,
    history: [],
  };

  return {
    async speak(text: string, options?: NarrationOptions): Promise<void> {
      activity.utterances += 1;
      activity.lastText = text;
      activity.history.push(text);
      if (activity.history.length > NARRATION_HISTORY_LIMIT) activity.history.shift();
      try {
        onActivity({ ...activity, history: [...activity.history] });
      } catch {
        // A readout that throws is a readout problem. It must not silence the narration.
      }
      await inner.speak(text, options);
    },
    stop(): void {
      inner.stop();
    },
    async replayLast(): Promise<void> {
      await inner.replayLast();
    },
    setEnabled(enabled: boolean): void {
      inner.setEnabled(enabled);
    },
    setVolume(volume: number): void {
      inner.setVolume(volume);
    },
    setVoiceProfile(profile: string): void {
      inner.setVoiceProfile(profile);
    },
  };
}
